// 마을 장비 상점 단계(S7, docs/STAGE-WORLD-PRD.md 5.2). 데이터와 순수 계산만(three·DOM 없음).
// 마을②~⑥의 잡화상(방어구·장신구)·대장간(무기)이 그 마을 단계 장비를 판다. 이전 마을 품목은 워프로 그 마을에 가서 산다(품목 = 지금 마을).
// 설계 기준(파밍 동기를 깨지 않는다): "드롭보다 약하게, 같은 레벨 희귀 상당".
//   · 등급 = 희귀(배율 1.25), 아이템 레벨 = 그 마을 지역의 시작 레벨(지역 n 일반 베이스 정의 레벨 = 같은 지역 노말 드롭의 아이템 레벨)
//   · 옵션 = 1줄 고정, 값 = 옵션 범위의 최저값 → 같은 지역 노말 희귀 드롭(옵션 1~2줄, 값 범위 전체)보다 강할 수 없다(townShop.test)
//   · 장신구(마을③ "보석 박힌 장신구")는 소켓 1칸에 깨진 청옥(1등급)이 박혀 나온다 — 보석은 실제 효과가 있고 빼면 보석함으로 간다
// 가격 = 부위 기본 가격(SHOP_PRICE.gear) × 그 마을 상점 단계 배율(SHOP_TIER_PRICE_MULT). 같은 품목을 여러 번 살 수 있다(재고 없음).
import { SHOP_PRICE, SHOP_TIER_PRICE_MULT } from './economy';
import { ENHANCE_TABLE, SOCKET_CAP, affixesFor, gearBase } from './items';
import { REGIONS_90D } from './world';
import { gemCode } from './consumables';
import type { AffixId, EquipDef, EquipSlot, HeroId, RegionNo } from './types';
import type { GearItem } from '../gear';
import type { NpcRole } from '../rpg';

/**
 * 지금 무기를 장착할 수 있는 영웅 = 플레이 가능한 영웅(영웅별 무기 칸, lyra-weapon-slot: 카엘 = equipped.weapon, 라이라 = hw.lyra).
 * 장착할 수 없는 무기를 팔면 돈만 나가므로 상점·장부가 같은 조건으로 막는다. 새 영웅이 플레이 가능해지면 이 목록에 넣는다.
 */
export const WEAPON_EQUIP_HEROES: readonly HeroId[] = ['kael', 'lyra'];
/** 그 영웅이 이 품목을 살 수 있는가(무기는 장착 가능한 영웅만) */
export function townGearBuyableBy(o: Pick<TownGearOffer, 'slot'>, hero: HeroId): boolean {
  return o.slot !== 'weapon' || WEAPON_EQUIP_HEROES.includes(hero);
}

/**
 * 마을 장비 상점이 있는 마을. 마을①도 2026-10-26 장비 통합(ECONOMY 14절)부터 6부위 장비를 판다 —
 * 옛 상점 품목(shared/rpg ITEMS: 여행자·사슬·진홍 갑옷, 훈련·강철 대검, 잿불 단조)은 더 팔지 않는다
 */
export const TOWN_GEAR_TOWNS: readonly number[] = [1, 2, 3, 4, 5, 6];
const HEROES: readonly HeroId[] = ['kael', 'lyra', 'seren', 'brann'];
const HERO_WEAPON: Readonly<Record<HeroId, string>> = { kael: '대검', lyra: '장궁', seren: '지팡이', brann: '룬 해머', lumen: '성광 지팡이' };

interface Line {
  readonly slot: EquipSlot;
  readonly name: string;
  readonly affix: AffixId;
  readonly desc: string;
}
/** 마을별 품목(이름·고정 옵션·한 줄 설명). 옵션은 그 부위가 굴릴 수 있는 옵션 중 하나여야 한다(townShop.test) */
const LINES: Readonly<Record<number, readonly Line[]>> = {
  // 마을① 바람결: 첫 6부위 한 벌(여행자 가죽). 목걸이까지 판다(보석은 박혀 나오지 않는다 — 보석은 마을③부터)
  1: [
    { slot: 'weapon', name: '바람결', affix: 'skillDmg', desc: '바람결 대장간이 처음 벼려 주는 무기. 스킬이 조금 세진다.' },
    { slot: 'helm', name: '바람결 가죽 두건', affix: 'def', desc: '질긴 가죽을 덧댄 여행자 두건.' },
    { slot: 'armor', name: '바람결 가죽 갑옷', affix: 'def', desc: '가볍게 덧댄 여행자 가죽 갑옷.' },
    { slot: 'gloves', name: '바람결 가죽 장갑', affix: 'atkPct', desc: '손목을 단단히 잡아 주는 가죽 장갑.' },
    { slot: 'boots', name: '바람결 가죽 장화', affix: 'moveSpd', desc: '오래 걸어도 발이 편한 여행자 장화.' },
    { slot: 'amulet', name: '바람결 부적 목걸이', affix: 'xp', desc: '마을 사람들이 무사를 빌며 건네는 부적. 경험치가 조금 더 붙는다.' },
  ],
  2: [
    { slot: 'weapon', name: '흑요석', affix: 'atkPct', desc: '잿불에 담금질한 흑요석 날. 공격력이 조금 오른다.' },
    { slot: 'helm', name: '잿불 판금 투구', affix: 'def', desc: '열기를 막는 두꺼운 판금 투구.' },
    { slot: 'armor', name: '잿불 판금 갑옷', affix: 'def', desc: '잿불의 열기 속에서도 버티는 판금 갑옷.' },
    { slot: 'gloves', name: '잿불 판금 건틀릿', affix: 'atkPct', desc: '쇠를 다루던 손에 맞춘 건틀릿.' },
    { slot: 'boots', name: '잿불 판금 장화', affix: 'def', desc: '뜨거운 현무암을 밟아도 견디는 장화.' },
  ],
  3: [
    { slot: 'weapon', name: '서리 강철', affix: 'crit', desc: '차갑게 벼린 강철. 치명타가 조금 잘 난다.' },
    { slot: 'helm', name: '서리 강철 투구', affix: 'hpPct', desc: '눈보라를 막는 털 안감 투구.' },
    { slot: 'armor', name: '서리 강철 갑옷', affix: 'hpPct', desc: '항구 경비대가 입는 서리 강철 갑옷.' },
    { slot: 'gloves', name: '서리 강철 건틀릿', affix: 'crit', desc: '얼음 위에서도 손이 미끄러지지 않는다.' },
    { slot: 'boots', name: '서리 강철 장화', affix: 'hpPct', desc: '부두 얼음판을 버티는 징 박힌 장화.' },
    { slot: 'amulet', name: '보석 박힌 서리 목걸이', affix: 'mpRegen', desc: '깨진 청옥이 박혀 나온다(보석상에서 바꿀 수 있다).' },
  ],
  4: [
    { slot: 'weapon', name: '독니', affix: 'lifesteal', desc: '늪 짐승 송곳니를 박은 무기. 벤 만큼 조금 흡수한다.' },
    { slot: 'helm', name: '늪 비늘 투구', affix: 'skillDmg', desc: '늪 도마뱀 비늘을 엮은 투구.' },
    { slot: 'armor', name: '늪 비늘 갑옷', affix: 'damageTaken', desc: '독안개를 걸러 주는 비늘 갑옷.' },
    { slot: 'gloves', name: '늪 비늘 장갑', affix: 'critDmg', desc: '비늘 끝이 갈고리처럼 선 장갑.' },
    { slot: 'boots', name: '늪 비늘 장화', affix: 'moveSpd', desc: '진흙에 빠지지 않는 가벼운 장화.' },
  ],
  // 마을⑤ 오아시스 대상단(3개월 C2): 마을④ 줄과 같은 규칙(희귀·옵션 1줄 최저값). 홀수 마을(①③⑤)은 목걸이까지 판다(⑤는 깨진 청옥이 박혀 나온다)
  5: [
    { slot: 'weapon', name: '일몰', affix: 'awakenGauge', desc: '해질녘 금빛으로 벼린 대상단의 곡날. 각성 게이지가 조금 빨리 찬다.' },
    { slot: 'helm', name: '대상단 터번 투구', affix: 'cooldown', desc: '모래바람을 막는 천을 두른 투구. 스킬 재사용이 조금 줄어든다.' },
    { slot: 'armor', name: '대상단 비단 갑옷', affix: 'mpRegen', desc: '낮엔 시원하고 밤엔 따뜻한 천 갑옷.' },
    { slot: 'gloves', name: '사막 매듭 장갑', affix: 'critDmg', desc: '고삐를 쥐던 손에 맞춘 매듭 장갑.' },
    { slot: 'boots', name: '모래 걸음 장화', affix: 'gold', desc: '모래에 빠지지 않는 넓은 밑창. 대상단 길 위에서 골드를 더 번다.' },
    { slot: 'amulet', name: '청록 샘물 목걸이', affix: 'dropRate', desc: '오아시스 샘물빛 구슬. 깨진 청옥이 박혀 나온다(보석상에서 바꿀 수 있다).' },
  ],
  // 마을⑥ 하늘 요새: 지역6 입구 장비. 짝수 마을(②④⑥)은 목걸이 없이 5부위
  6: [
    { slot: 'weapon', name: '구름 가르개', affix: 'crit', desc: '요새 대장간이 구름 위에서 벼린 흰 날. 치명타가 조금 잘 난다.' },
    { slot: 'helm', name: '하늘 강철 투구', affix: 'hpPct', desc: '바람을 가르는 날개 장식 투구.' },
    { slot: 'armor', name: '하늘 강철 갑옷', affix: 'damageTaken', desc: '요새 수비대의 흰 강철 갑옷. 받는 피해가 조금 줄어든다.' },
    { slot: 'gloves', name: '하늘 강철 건틀릿', affix: 'atkPct', desc: '비공정 밧줄을 잡던 건틀릿.' },
    { slot: 'boots', name: '구름 걸음 장화', affix: 'moveSpd', desc: '구름 위 부두를 가볍게 딛는 장화.' },
  ],
};
/** 마을 → 그 마을 지역(마을 n = 지역 n). 아이템 레벨은 지역 시작 레벨 */
function regionOfTown(town: number): RegionNo { return Math.max(1, Math.min(6, town)) as RegionNo; }
export function townGearLevel(town: number): number { return REGIONS_90D[regionOfTown(town) - 1].levels[0]; }
function sellerOf(slot: EquipSlot): NpcRole { return slot === 'weapon' ? 'forge' : 'shop'; }
const SLOT_NAME: Readonly<Record<EquipSlot, string>> = { weapon: '무기', helm: '투구', armor: '갑옷', gloves: '장갑', boots: '신발', amulet: '목걸이' };

/**
 * 상점 장비 정의(드롭 후보가 아니다: gear.ts candidates는 EQUIPS만 본다). id = eq_shop_t<마을>_<부위>[_<영웅>].
 * 등급 정의는 희귀, 기본 능력치는 gearBase(부위, 마을 레벨) — 드롭 개체와 같은 곡선
 */
export const SHOP_EQUIPS: readonly EquipDef[] = (() => {
  const out: EquipDef[] = [];
  for (const town of TOWN_GEAR_TOWNS) {
    const level = townGearLevel(town), region = regionOfTown(town);
    const tier = (region <= 2 ? 1 : region <= 4 ? 2 : 3) as 1 | 2 | 3;
    for (const l of LINES[town]) {
      const base = gearBase(l.slot, level);
      if (l.slot === 'weapon') {
        for (const h of HEROES) out.push({ id: `eq_shop_t${town}_weapon_${h}`, name: `${l.name} ${HERO_WEAPON[h]}`, slot: 'weapon', heroLock: h, grade: 'rare', level, region, base, affixPool: [l.affix], sockets: SOCKET_CAP.rare, tier });
      } else {
        out.push({ id: `eq_shop_t${town}_${l.slot}`, name: l.name, slot: l.slot, grade: 'rare', level, region, base, affixPool: [l.affix], sockets: SOCKET_CAP.rare, tier });
      }
    }
  }
  return out;
})();

/** 상점 품목 하나(무기는 영웅별 정의를 고른다) */
export interface TownGearOffer {
  /** 품목 id(연산·저장에 쓴다): shop_t<마을>_<부위> */
  readonly id: string;
  readonly town: number;
  readonly slot: EquipSlot;
  readonly seller: NpcRole;
  readonly affix: AffixId;
  readonly desc: string;
  /** 표시 이름(무기는 영웅 무기 이름이 붙는다 — offerName) */
  readonly name: string;
}
export const TOWN_GEAR: readonly TownGearOffer[] = TOWN_GEAR_TOWNS.flatMap((town) => LINES[town].map((l): TownGearOffer => ({
  id: `shop_t${town}_${l.slot}`, town, slot: l.slot, seller: sellerOf(l.slot), affix: l.affix, desc: l.desc, name: l.name,
})));
const OFFER_BY_ID = new Map<string, TownGearOffer>(TOWN_GEAR.map((o) => [o.id, o]));
export function townGearOffer(id: string): TownGearOffer | undefined { return OFFER_BY_ID.get(id); }
/** 그 마을에서 그 NPC가 파는 품목(마을 장비 상점이 없는 마을이면 빈 목록) */
export function townGearFor(town: number, seller: NpcRole): TownGearOffer[] {
  return TOWN_GEAR.filter((o) => o.town === town && o.seller === seller);
}
/** 영웅에 맞는 정의 id(무기는 영웅 전용, 나머지는 공용) */
export function offerDefId(o: TownGearOffer, hero: HeroId): string {
  return o.slot === 'weapon' ? `eq_shop_t${o.town}_weapon_${hero}` : `eq_shop_t${o.town}_${o.slot}`;
}
export function offerName(o: TownGearOffer, hero: HeroId): string {
  return o.slot === 'weapon' ? `${o.name} ${HERO_WEAPON[hero] ?? '무기'}` : o.name;
}
/** 부위 이름(표시) */
export function slotName(slot: EquipSlot): string { return SLOT_NAME[slot]; }
/** 가격(골드): 부위 기본 가격 × 그 마을 상점 단계 배율 */
export function townGearPrice(o: TownGearOffer): number {
  const t = Math.max(1, Math.min(SHOP_TIER_PRICE_MULT.length, Math.floor(o.town)));
  return Math.round(SHOP_PRICE.gear[o.slot] * SHOP_TIER_PRICE_MULT[t - 1]);
}
/** 고정 옵션 값(그 옵션 범위의 최저값) */
export function offerAffixValue(o: TownGearOffer): number {
  const a = affixesFor(o.slot).find((x) => x.id === o.affix);
  return a ? a.range[0] : 0;
}
/** 장신구에 박혀 나오는 보석(깨진 청옥, 1등급). 다른 부위는 빈 소켓 */
export const SHOP_AMULET_GEM = gemCode(1, 1);

/**
 * 산 장비 개체(결정적): 같은 품목·영웅·uid면 같은 개체. 장부(gearLedger buyGear)가 연산 id에서 uid를 만들어 부른다 —
 * 클라이언트가 보낸 개체를 믿지 않고 품목 id로 다시 만든다. 강화 0, 천장 카운터 0.
 */
export function townGearItem(o: TownGearOffer, hero: HeroId, uid: string): GearItem {
  const item: GearItem = {
    uid, def: offerDefId(o, hero), grade: 'rare', ilvl: townGearLevel(o.town),
    affixes: [{ id: o.affix, v: offerAffixValue(o) }], enh: 0, pity: new Array<number>(ENHANCE_TABLE.length + 1).fill(0),
  };
  // 보석 박힌 장신구는 마을③(보석상)부터. 마을① 목걸이는 빈 소켓
  if (o.slot === 'amulet' && o.town >= 3) item.sock = [SHOP_AMULET_GEM];
  return item;
}
